import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../../services/auth.service';

export const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const newPass = control.get('newPassword')?.value;
  const confirmPass = control.get('confirmPassword')?.value;
  if (newPass && confirmPass && newPass !== confirmPass) {
    return { passwordMismatch: true };
  }
  return null;
};

@Component({
  selector: 'app-dlg-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule],
  templateUrl: './dlg-change-password.component.html',
})
export class DlgChangePasswordComponent implements OnInit {
  private readonly dialogRef: MatDialogRef<DlgChangePasswordComponent> =
    inject(MatDialogRef<DlgChangePasswordComponent>);
  private readonly authService: AuthService = inject(AuthService);
  private readonly toastr: ToastrService = inject(ToastrService);

  changePasswordForm: FormGroup;
  isSubmitting = false;

  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  isCurrentPasswordReadOnly = true;

  constructor() {
    this.changePasswordForm = new FormGroup(
      {
        currentPassword: new FormControl('', [Validators.required]),
        newPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
      },
      { validators: passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.changePasswordForm.reset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }

  onCurrentPasswordFocus() {
    this.isCurrentPasswordReadOnly = false;
  }

  toggleShowCurrentPassword() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleShowNewPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  async onSubmit() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      if (this.changePasswordForm.errors?.['passwordMismatch']) {
        this.toastr.error(
          'New password and confirmation password do not match.',
          'Validation Error'
        );
      }
      return;
    }

    const { currentPassword, newPassword } = this.changePasswordForm.value;
    this.isSubmitting = true;

    try {
      await this.authService.changePassword(currentPassword, newPassword);
      this.toastr.success('Password updated successfully!', 'Success');
      this.isSubmitting = false;
      this.dialogRef.close(true);
    } catch (error: any) {
      this.toastr.error(
        error.message || 'Error updating password. Please check your credentials.',
        'Password Update Failed'
      );
      this.isSubmitting = false;
    }
  }
}
